import React, { useEffect, useLayoutEffect } from 'react';

import { Box } from '@rbilabs/universal-components';
import { ProductCustomization } from 'features/menu/components/product-detail/product-customization';
import { CloseConfirmationDialog } from 'features/menu/components/product-detail/product-detail-flow/dialogs/close-confirmation-dialog';
import { UnsavedChangesDialog } from 'features/menu/components/product-detail/product-detail-flow/dialogs/unsaved-changes-dialog';
import { ProductHome } from 'features/menu/components/product-detail/product-home';
import {
  ProductDetailFlowContent,
  getBackgroundColorByStep,
  productDetailFlowContainerStyles,
} from 'features/menu/components/product-detail/styled';
import { isCustomization } from 'features/menu/components/product-detail/utils';
import { useProductWizardContext } from 'features/menu/components/product-wizard';
import { useProductWizardFlow } from 'features/menu/components/product-wizard/use-product-wizard-flow';
import Animated, {
  FadeIn,
  LayoutAnimationConfig,
  SlideInLeft,
  SlideInRight,
} from 'react-native-reanimated';

import {
  maybePlayAudioOnce,
  useMaybePlayAudioOnceOnMount,
} from 'components/app-takeover/play-audio-once';
import { useAppTakeoverPdp } from 'components/app-takeover/use-app-takeover';

export const FADE_IN_ANIMATION_DURATION = 700;
export const SLIDE_IN_ANIMATION_DURATION = 300;

export const ProductDetail = () => {
  const { currentFlowStep, id } = useProductWizardFlow();
  const { flowDirection } = useProductWizardContext();
  const { backgroundColor: takeoverBg, audio, additionalAudioSteps } = useAppTakeoverPdp(id);

  useMaybePlayAudioOnceOnMount(audio);

  useEffect(() => {
    maybePlayAudioOnce(additionalAudioSteps?.[currentFlowStep.step]);
  }, [additionalAudioSteps, currentFlowStep.step]);

  const isMountedRef = React.useRef<boolean>(false);

  const Animation = flowDirection === 'backward' ? SlideInLeft : SlideInRight;

  const productDetailFlowKey = `${currentFlowStep.step}-${currentFlowStep.slotKey}-${flowDirection}`;

  // LayoutAnimationConfig skipEntering alternative logic for web devices
  useLayoutEffect(() => {
    isMountedRef.current = true;
  });

  return (
    <Box testID="product-detail" flex={1}>
      <LayoutAnimationConfig skipEntering>
        <Animated.View
          key={productDetailFlowKey}
          style={productDetailFlowContainerStyles}
          entering={FadeIn.duration(FADE_IN_ANIMATION_DURATION)}
        >
          <Animated.View
            entering={
              isMountedRef.current ? Animation.duration(SLIDE_IN_ANIMATION_DURATION) : undefined
            }
            style={{
              ...productDetailFlowContainerStyles,
              backgroundColor: takeoverBg ?? getBackgroundColorByStep(currentFlowStep.step),
            }}
          >
            <ProductDetailFlowContent testID="product-detail-flow-content">
              <ProductDetailStep />
            </ProductDetailFlowContent>
          </Animated.View>
        </Animated.View>
      </LayoutAnimationConfig>

      <CloseConfirmationDialog />
      <UnsavedChangesDialog />
    </Box>
  );
};

const ProductDetailStep = () => {
  const { currentFlowStep } = useProductWizardFlow();

  if (isCustomization(currentFlowStep.step)) {
    return <ProductCustomization />;
  }

  return <ProductHome />;
};
